import type { LazyQueryHookOptions, QueryHookOptions } from "@apollo/client";
import { useLazyQuery, useQuery, gql } from "@apollo/client";

import type { PageInfoFragment } from "@shared/data/fragments/PageInfoFragment/PageInfoFragment.fragment";
import { PAGE_INFO_FRAGMENT } from "@shared/data/fragments/PageInfoFragment/PageInfoFragment.fragment";
import type { Ingredient as IngredientType } from "@shared/types/graphqlGenerated";

const Ingredients_Shared_Query = gql`
  query Ingredients_Shared_Query(
    $first: Int
    $last: Int
    $after: String
    $before: String
    $filters: IngredientFilterObject
    $sort: [IngredientSort!]
  ) {
    viewer {
      ingredients(
        first: $first
        last: $last
        after: $after
        before: $before
        filters: $filters
        sort: $sort
      ) {
        nodes {
          id
          name
          nameTitleized
          children {
            nodes {
              id
              name
            }
            pageInfo {
              ...PageInfoFragment
            }
            totalCount
          }
        }
        pageInfo {
          ...PageInfoFragment
        }
        totalCount
      }
    }
  }
  ${PAGE_INFO_FRAGMENT}
`;

type Ingredient = Pick<IngredientType, "id" | "name">;

interface IngredientWithRelations extends Ingredient {
  children: {
    nodes: Ingredient[];
    pageInfo: PageInfoFragment;
    totalCount: number;
  };
}
interface IngredientData {
  viewer: {
    ingredients: {
      nodes: IngredientWithRelations[];
      pageInfo: PageInfoFragment;
      totalCount: number;
    };
  };
}

interface IngredientFilterObject {
  ingredientId?: string;
  ingredientNames?: string[];
  topLevel?: boolean;
}

interface IngredientSortObject {
  field: "NAME" | "SEARCH_POPULARITY";
  direction: "ASC" | "DESC";
}

interface Variables {
  first?: number;
  last?: number;
  after?: string;
  before?: string;
  filters?: IngredientFilterObject;
  sort?: [IngredientSortObject];
}

const useIngredientsQuery = (options?: QueryHookOptions<IngredientData, Variables>) =>
  useQuery(Ingredients_Shared_Query, options);

const useIngredientsLazyQuery = (options?: LazyQueryHookOptions<IngredientData, Variables>) =>
  useLazyQuery(Ingredients_Shared_Query, options);

export type { IngredientData };

/* eslint-disable @fullscript/gql-no-manual-hook-declaration */
export { useIngredientsQuery, useIngredientsLazyQuery, Ingredients_Shared_Query };
